import type { BaseColumnConfig, SortState } from './types';

function compare(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

/**
 * Applies grid sort state to rows the caller already holds in full.
 *
 * `DataGrid` renders exactly the array it is handed — it records sort intent in
 * the URL but never reorders. Routes that page server-side answer that intent in
 * SQL; pages whose rows arrive whole (an aggregate report, a small config list)
 * answer it with this instead, so their sortable headers actually sort.
 *
 * Nulls sort last in both directions: "no value" is not smaller than every
 * value, it is absent, and burying it beats leading with it.
 */
export function sortRowsBy<TRow>(
  rows: TRow[],
  sort: SortState,
  columns: BaseColumnConfig<TRow>[],
): TRow[] {
  if (sort.length === 0) return rows;

  const accessors = new Map(columns.map((c) => [c.id, c.accessor]));
  const active = sort.filter((pair) => accessors.has(pair.columnId));
  if (active.length === 0) return rows;

  return [...rows].sort((rowA, rowB) => {
    for (const { columnId, direction } of active) {
      const accessor = accessors.get(columnId)!;
      const a = accessor(rowA);
      const b = accessor(rowB);

      // Null ordering is deliberately not negated by direction — flipping to
      // descending should not drag every blank row to the top.
      if (a == null && b == null) continue;
      if (a == null) return 1;
      if (b == null) return -1;

      const result = compare(a, b);
      if (result !== 0) return direction === 'desc' ? -result : result;
    }
    return 0;
  });
}
